// Granular permissions — client mirror of the DB's has_perm() resolution
// (mig 20260706150000). Spec: docs/specs/custom-roles-permissions.md
//
// A permission is `<module_key>:<action>`. Module keys are the MODULES[]
// registry keys (src/lib/modules.ts); the DB's module_keys table mirrors
// them (permissions.test.ts pins the two lists together). The ONE
// implication rule: any grant implies view — the DB normalisation trigger
// enforces it at write time, so maps read back always carry 'view'.

import type { UserRole } from '@/types/database'

// Full action vocabulary the DB accepts. V1 of the roles editor only
// surfaces the four broad ones (PERM_ACTIONS_V1); the specialised verbs
// light up per module as the P2 RLS sweeps migrate that module's
// policies onto has_perm(). (Not exported — export it when the first
// consumer outside this file appears.)
const PERM_ACTIONS = [
  'view', 'edit', 'delete', 'approve', 'dispatch',
  'print', 'export', 'override', 'manage',
] as const
export type PermAction = typeof PERM_ACTIONS[number]

export const PERM_ACTIONS_V1 = ['view', 'edit', 'delete', 'manage'] as const

export const PERM_ACTION_LABELS: Record<PermAction, string> = {
  view:     'View',
  edit:     'Edit',
  delete:   'Delete',
  approve:  'Approve',
  dispatch: 'Dispatch',
  print:    'Print',
  export:   'Export',
  override: 'Override',
  manage:   'Manage',
}

// The 'admin' pseudo-module's verbs. Custom roles may hold 'members'
// (member admin is delegable); 'roles' and 'billing' are reserved for the
// built-in admin tier — the DB normalisation trigger rejects them in a
// custom map.
export type AdminAction = 'members' | 'roles' | 'billing'

// { module_key: ['view','edit',…] } — the JSONB shape stored on
// tenant_roles.permissions / role_permission_defaults.permissions.
export type PermissionMap = Record<string, string[]>

// Shape returned by the get_my_permissions() RPC. `all` is the platform-
// admin mode (passes everything, subject to the platform_view claim on
// the server side); `custom` says a tenant_roles row is the authority.
export interface MyPermissions {
  all:      boolean
  custom:   boolean
  role_key: string | null
  label:    string | null
  permissions: PermissionMap
}

// A tenant_roles row (mig 20260706150000).
export interface TenantRole {
  tenant_role_id: string
  tenant_id:      string
  role_key:       string
  label:          string
  description:    string | null
  permissions:    PermissionMap
  fallback_role:  UserRole
  version:        number
  archived_at:    string | null
  created_at:     string
}

// Pure resolution — mirrors has_perm(): fail-closed on a missing map,
// platform-admin mode passes everything.
export function canFrom(
  p: MyPermissions | null | undefined,
  module: string,
  action: PermAction | AdminAction,
): boolean {
  if (!p) return false
  if (p.all) return true
  return (p.permissions[module] ?? []).includes(action)
}

// The custom role's view-set, used by hasModule() to replace the
// enabled_modules / role-defaults layer for custom-role holders.
export function viewableModules(p: MyPermissions): string[] {
  return Object.entries(p.permissions)
    .filter(([k, actions]) => k !== 'admin' && actions.includes('view'))
    .map(([k]) => k)
}

// ---------------------------------------------------------------------------
// Role-editor helpers (pure — the RolesManager UI delegates here; tested
// in permissions.test.ts).
// ---------------------------------------------------------------------------

// Mirrors the DB's tenant_roles_key_shape CHECK. Internal — role keys are
// only minted through buildTenantRolePayload below.
const ROLE_KEY_RE = /^[a-z][a-z0-9-]{1,40}$/

function slugifyRoleKey(label: string): string {
  return label.toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/^([^a-z])/, 'r$1')
    .slice(0, 40)
}

// Toggle one action on a module, applying the implication rule both ways:
// granting any action grants view (mirrors the DB normalisation trigger);
// dropping view drops the whole module (the other actions would only
// re-imply it server-side).
export function togglePermAction(map: PermissionMap, moduleKey: string, action: PermAction): PermissionMap {
  const current = new Set(map[moduleKey] ?? [])
  if (current.has(action)) {
    current.delete(action)
    if (action === 'view') current.clear()
  } else {
    current.add(action)
    current.add('view')
  }
  const next = { ...map }
  if (current.size === 0) delete next[moduleKey]
  else next[moduleKey] = Array.from(current)
  return next
}

// Validate + assemble the tenant_roles write payload, including the minted
// role_key (creates use it; edits discard it — the key is immutable).
// Throws operator-readable messages (surfaced inline by the editor,
// never Sentry-worthy).
export function buildTenantRolePayload(input: {
  label: string
  description: string
  memberAdmin: boolean
  perms: PermissionMap
}): { role_key: string; label: string; description: string | null; permissions: PermissionMap } {
  const label = input.label.trim()
  if (!label) throw new Error('Give the role a name.')
  if (Object.keys(input.perms).length === 0) throw new Error('Grant at least one module.')
  const role_key = slugifyRoleKey(label)
  if (!ROLE_KEY_RE.test(role_key)) throw new Error('Role name must contain at least two letters/digits.')
  return {
    role_key,
    label,
    description: input.description.trim() || null,
    permissions: input.memberAdmin ? { ...input.perms, admin: ['members'] } : input.perms,
  }
}
